import React from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { Eye, EyeOff } from 'lucide-react';
import toast from 'react-hot-toast';
import api, { API_ENDPOINTS } from '../config/api';

const JobHideToggleButton = ({
  jobId,
  jobTitle,
  isHidden = false,
  invalidateKeys = [],
  className = '',
}) => {
  const queryClient = useQueryClient();

  const invalidate = () => {
    invalidateKeys.forEach((key) => {
      queryClient.invalidateQueries(key);
    });
  };

  const hideMutation = useMutation(
    () => api.put(API_ENDPOINTS.JOBS.HIDE(jobId)),
    {
      onSuccess: () => {
        invalidate();
        queryClient.invalidateQueries(['notifications']);
        queryClient.invalidateQueries(['unreadNotifications']);
        toast.success(`"${jobTitle}" hidden from job listings`);
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to hide job');
      },
    }
  );

  const unhideMutation = useMutation(
    () => api.put(API_ENDPOINTS.JOBS.UNHIDE(jobId)),
    {
      onSuccess: () => {
        invalidate();
        queryClient.invalidateQueries(['notifications']);
        queryClient.invalidateQueries(['unreadNotifications']);
        toast.success(`"${jobTitle}" is visible on job listings again`);
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to unhide job');
      },
    }
  );

  const isLoading = hideMutation.isLoading || unhideMutation.isLoading;

  const handleClick = () => {
    if (isLoading) return;
    if (isHidden) {
      unhideMutation.mutate();
    } else {
      hideMutation.mutate();
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={isLoading}
      className={`p-2 text-gray-600 dark:text-gray-400 hover:text-accent transition-colors duration-200 disabled:opacity-50 ${className}`}
      title={isHidden ? 'Show on job listings' : 'Hide from job listings'}
      aria-label={isHidden ? 'Unhide job from listings' : 'Hide job from listings'}
    >
      {isHidden ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
    </button>
  );
};

export default JobHideToggleButton;
